import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface ContributionGraphProps {
    data: Record<string, number>; // date "YYYY-MM-DD" -> count
    year?: number;
}

const ContributionGraph: React.FC<ContributionGraphProps> = ({ data, year = new Date().getFullYear() }) => {
    // State for selected date detail (Click interaction)
    const [selectedDay, setSelectedDay] = useState<{ date: string; count: number } | null>(null);

    // Generate calendar data
    // Logic: Start from Current Month -> End of Year.
    const calendarGrid = useMemo(() => {
        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31);

        const dates: Date[] = [];
        let currentDate = new Date(startDate);
        // Normalize time to avoid DST issues
        currentDate.setHours(0, 0, 0, 0);
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);

        // Limit loop to avoid infinite in case of error
        let safety = 0;
        while (currentDate <= endDateTime && safety < 400) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
            safety++;
        }
        return dates;
    }, [year]);

    // Format YYYY-MM-DD (Robust Local Time)
    const formatDateKey = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Levels 0-4
    const getLevel = (count: number) => {
        if (count === 0) return 0;
        if (count === 1) return 1;
        if (count <= 3) return 2;
        if (count <= 5) return 3;
        return 4;
    };

    const getColorStyle = (level: number) => {
        switch (level) {
            case 1: return 'bg-green-200 border-green-300 dark:bg-[#0e4429] dark:border-[#0e4429]';
            case 2: return 'bg-green-300 border-green-400 dark:bg-[#006d32] dark:border-[#006d32]';
            case 3: return 'bg-green-400 border-green-500 dark:bg-[#26a641] dark:border-[#26a641]';
            case 4: return 'bg-green-500 border-green-600 dark:bg-[#39d353] dark:border-[#39d353]';
            default: return 'bg-gray-100 border-gray-200 dark:bg-[#161b22] dark:border-[#161b22]';
        }
    };

    const weeks = useMemo(() => {
        const cols: (Date | null)[][] = [];
        let colIndex = 0;

        calendarGrid.forEach((date) => {
            const dow = date.getDay();
            const rowIndex = dow === 0 ? 6 : dow - 1;

            if (!cols[colIndex]) cols[colIndex] = new Array(7).fill(null);

            cols[colIndex][rowIndex] = date;

            if (rowIndex === 6) {
                colIndex++;
            }
        });
        // Filter out empty columns to avoid trailing "half box"
        return cols.filter(col => col.some(day => day !== null));
    }, [calendarGrid]);

    const scrollRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (scrollRef.current) {
            const todayCell = scrollRef.current.querySelector('#today-cell');
            if (todayCell) {
                // Scroll to make Today visible on the LEFT (or near left)
                // We subtract a bit of padding (e.g. 50px) to give context
                const offset = (todayCell as HTMLElement).offsetLeft;
                scrollRef.current.scrollLeft = Math.max(0, offset - 40);
            } else {
                scrollRef.current.scrollLeft = 0;
            }
        }
    }, [calendarGrid]);

    const [tooltip, setTooltip] = useState<{ x: number, y: number, content: string } | null>(null);

    const handleMouseEnter = (e: React.MouseEvent, content: string) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip({
            x: rect.left + rect.width / 2,
            y: rect.top,
            content
        });
    };

    const handleMouseLeave = () => {
        setTooltip(null);
    };

    return (
        <div ref={scrollRef} className="w-full select-none overflow-x-auto no-scrollbar pb-2 pr-4 relative touch-pan-x">
            <div className="flex items-end gap-[1px] min-w-max">
                {/* Labels: Mon/Wed/Fri */}
                <div className="flex flex-col gap-[1px] text-[9px] text-gray-400 dark:text-gray-500 font-mono font-semibold py-[1px] min-w-[20px] text-right sticky left-0 z-10 pr-2">
                    <div className="h-[10px] md:h-[11px] flex items-center justify-end leading-none">Mon</div>
                    <div className="h-[10px] md:h-[11px]"></div>
                    <div className="h-[10px] md:h-[11px] flex items-center justify-end leading-none">Wed</div>
                    <div className="h-[10px] md:h-[11px]"></div>
                    <div className="h-[10px] md:h-[11px] flex items-center justify-end leading-none">Fri</div>
                    <div className="h-[10px] md:h-[11px]"></div>
                    <div className="h-[10px] md:h-[11px]"></div>
                </div>

                {/* Graph Area */}
                <div className="flex-1 flex flex-col gap-1">
                    {/* Month Labels */}
                    <div className="flex gap-[1px] w-full justify-start text-[9px] md:text-[10px] text-gray-400 font-mono pl-1 mb-1 overflow-visible h-[12px]">
                        {(() => {
                            let lastLabelIdx = -10;
                            return weeks.map((week, wIdx) => {
                                const firstDay = week.find(d => d !== null);
                                let label = null;

                                if (firstDay) {
                                    const days = week.filter(d => d !== null) as Date[];
                                    const monthStartDay = days.find(d => d.getDate() <= 7);

                                    if (monthStartDay) {
                                        if (wIdx - lastLabelIdx > 3) {
                                            label = monthStartDay.toLocaleString('en-US', { month: 'short' });
                                            lastLabelIdx = wIdx;
                                        }
                                    }
                                }

                                return (
                                    <div key={wIdx} className="w-[10px] md:w-[11px] flex-shrink-0 relative overflow-visible flex justify-center">
                                        {label && (
                                            <span className="absolute bottom-0 whitespace-nowrap" style={{ left: 0 }}>
                                                {label}
                                            </span>
                                        )}
                                    </div>
                                );
                            });
                        })()}
                    </div>

                    {/* Grid */}
                    <div className="flex gap-[1px] w-full justify-start py-[1px]">
                        {weeks.map((week, wIdx) => (
                            <div key={wIdx} className="flex flex-col gap-[1px] flex-shrink-0">
                                {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                                    const date = week[dayIndex];
                                    if (!date) return <div key={dayIndex} className="w-[10px] md:w-[11px] h-[10px] md:h-[11px]" />;

                                    const dateKey = formatDateKey(date);
                                    const count = data[dateKey] || 0;
                                    const level = getLevel(count);

                                    // Check if selected
                                    const isSelected = selectedDay?.date === dateKey;

                                    // Check if Today
                                    const isToday = dateKey === formatDateKey(new Date());

                                    // Format tooltip date: DD/MM/YYYY
                                    const tooltipDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
                                    const tooltipContent = `${count} bài kiểm tra vào ${tooltipDate}`;

                                    return (
                                        <div
                                            key={dayIndex}
                                            id={isToday ? "today-cell" : undefined}
                                            className={`w-[10px] md:w-[11px] h-[10px] md:h-[11px] rounded-[1.5px] md:rounded-[2px] border ${getColorStyle(level)} ${isSelected ? 'ring-2 ring-white z-10' : 'hover:ring-1 hover:ring-white/50'} ${isToday ? 'ring-1 ring-blue-400 dark:ring-blue-500' : ''} transition-all cursor-pointer relative group`}
                                            onMouseEnter={(e) => handleMouseEnter(e, tooltipContent)}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={() => setSelectedDay({ date: dateKey, count })}
                                        >
                                            {isToday && <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse z-10 shadow-sm" />}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer Summary (Dynamic) */}
            <div className="mt-4 pt-4 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs font-mono text-gray-400 sticky left-0 min-h-[32px]">
                <div className="font-semibold text-gray-300 transition-all duration-300">
                    {selectedDay ? (
                        <span>
                            {selectedDay.count > 0 ? (
                                <span className="text-gray-900 dark:text-white">
                                    {selectedDay.count} bài đã hoàn thành vào {datePart(selectedDay.date, 'd')} tháng {datePart(selectedDay.date, 'm')}, {datePart(selectedDay.date, 'y')}
                                </span>
                            ) : (
                                <span className="text-gray-400">
                                    Không có hoạt động vào {datePart(selectedDay.date, 'd')} tháng {datePart(selectedDay.date, 'm')}, {datePart(selectedDay.date, 'y')}
                                </span>
                            )}
                        </span>
                    ) : (
                        <span>
                            {Object.values(data).reduce((a, b) => a + b, 0)} bài kiểm tra đã hoàn thành {year === new Date().getFullYear() ? 'trong năm qua' : `trong năm ${year}`}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    <span>Ít</span>
                    <div className={`w-[10px] md:w-[11px] h-[10px] md:h-[11px] rounded-[1.5px] md:rounded-[2px] ${getColorStyle(0)}`} />
                    <div className={`w-[10px] md:w-[11px] h-[10px] md:h-[11px] rounded-[1.5px] md:rounded-[2px] ${getColorStyle(1)}`} />
                    <div className={`w-[10px] md:w-[11px] h-[10px] md:h-[11px] rounded-[1.5px] md:rounded-[2px] ${getColorStyle(2)}`} />
                    <div className={`w-[10px] md:w-[11px] h-[10px] md:h-[11px] rounded-[1.5px] md:rounded-[2px] ${getColorStyle(3)}`} />
                    <div className={`w-[10px] md:w-[11px] h-[10px] md:h-[11px] rounded-[1.5px] md:rounded-[2px] ${getColorStyle(4)}`} />
                    <span>Nhiều</span>
                </div>

                {/* Custom Floating Tooltip */}
                {tooltip && createPortal(
                    <div
                        className="fixed z-[9999] px-3 py-2 bg-gray-900/90 backdrop-blur-sm text-white text-xs rounded-lg shadow-xl pointer-events-none transform -translate-x-1/2 -translate-y-full transition-opacity border border-white/10"
                        style={{ left: tooltip.x, top: tooltip.y - 8 }}
                    >
                        <div className="font-medium whitespace-nowrap">{tooltip.content}</div>
                        {/* Arrow */}
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900/90 rotate-45 border-r border-b border-white/10"></div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
};

// Helper for date string parsing in render (avoid new Date(string) quirks)
function datePart(dateStr: string, part: 'd' | 'm' | 'y') {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (part === 'd') return d;
    if (part === 'm') return m;
    if (part === 'y') return y;
    return 0;
}

export default ContributionGraph;
